import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { BrandLedgerPage, exportBrandLedger, getBrandLedger } from '../../../../api/payment/brand-ledger.api';
import { Download } from 'lucide-react';
import { AdminButton, AdminCard, AdminSectionTitle } from '../../ui';
import { buildLedgerWorkbook, downloadWorkbook, ledgerFileName } from '../../../../utils/brand-ledger-export';
import { apiErrorMessage } from '../../../../utils/api-error-message';

const PAGE_SIZE = 50;
const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;
const fmtCa = (n?: number | null) => (n ? `${Number(n).toLocaleString('vi-VN')} Ca` : '');

/** Sổ dòng tiền sàn hợp tác creator (spec §4.4): chọn khoảng ngày giờ Việt Nam, xem theo trang, tổng theo loại, xuất Excel. */
const BrandLedgerSection: React.FC = () => {
  const [from, setFrom] = useState(() => dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [page, setPage] = useState(1);
  const [data, setData] = useState<BrandLedgerPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(
    async (target: number) => {
      if (!from || !to) return;
      setLoading(true);
      try {
        const res = await getBrandLedger(from, to, target, PAGE_SIZE);
        setData(res);
        setPage(res.page || target);
      } catch (e) {
        setData(null);
        toast.error(apiErrorMessage(e, 'Không tải được sổ dòng tiền.'));
      } finally {
        setLoading(false);
      }
    },
    [from, to]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = await exportBrandLedger(from, to);
      downloadWorkbook(buildLedgerWorkbook(all), ledgerFileName(from, to));
      toast.success(`Đã xuất ${(all.totalItems ?? 0).toLocaleString('vi-VN')} dòng.`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Không xuất được sổ dòng tiền.'));
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.totalItems / (data.pageSize || PAGE_SIZE))) : 1;

  return (
    <section className="adm-stack">
      <AdminSectionTitle
        title={`Sổ dòng tiền sàn hợp tác ${data ? `(${data.totalItems.toLocaleString('vi-VN')} dòng)` : ''}`.trim()}
        extra={
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
            <label className="adm-field">
              Từ ngày
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="adm-input" style={{ width: 156 }} />
            </label>
            <label className="adm-field">
              Đến ngày
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="adm-input" style={{ width: 156 }} />
            </label>
            <AdminButton variant="soft" icon={<Download size={18} />} onClick={handleExport} disabled={exporting}>
              {exporting ? 'Đang xuất…' : 'Xuất Excel'}
            </AdminButton>
          </div>
        }
      />

      <AdminCard flush>
        {data && (data.totals ?? []).length > 0 && (
          <div className="adm-panel" style={{ margin: '20px 20px 16px', padding: '16px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--adm-heading)' }}>Tổng theo loại</div>
            <div
              style={{
                marginTop: 8,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '4px 32px',
                fontSize: 13,
                lineHeight: '22px',
              }}
            >
              {(data.totals ?? []).map((t) => (
                <div key={t.kind} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span className="adm-muted">{t.kind}</span>
                  <span style={{ fontWeight: 600, color: 'var(--adm-heading)', textAlign: 'right' }}>
                    {fmtVnd(t.amountVnd)}
                    <span className="adm-muted"> {Number(t.amountCa || 0).toLocaleString('vi-VN')} Ca</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && !data ? (
          <p className="adm-muted" style={{ margin: 0, padding: 20, fontSize: 14 }}>Đang tải…</p>
        ) : !data || (data.items ?? []).length === 0 ? (
          <p className="adm-muted" style={{ margin: 0, padding: 20, fontSize: 14 }}>Không có dòng nào trong khoảng ngày này.</p>
        ) : (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Thời điểm</th>
                    <th>Loại</th>
                    <th>Chiến dịch</th>
                    <th>Người đặt</th>
                    <th>Creator</th>
                    <th className="adm-num">Số tiền</th>
                    <th className="adm-num">Số Ca</th>
                    <th>Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.items ?? []).map((i, idx) => (
                    <tr key={`${i.atVn}-${i.kind}-${idx}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{i.atVn}</td>
                      <td>{i.kind}</td>
                      <td>{i.campaignTitle ?? ''}</td>
                      <td>
                        {i.ordererName} · {i.ordererKind}
                        <div className="adm-sub">{i.ordererTaxCode ?? i.ordererIdentityName ?? ''}</div>
                      </td>
                      <td>
                        {i.creatorIdentityName ?? ''}
                        <div className="adm-sub">{i.creatorTaxOrId ?? ''}</div>
                      </td>
                      <td className="adm-num">{fmtVnd(i.amountVnd)}</td>
                      <td className="adm-num">{fmtCa(i.amountCa)}</td>
                      <td className="max-w-[240px] truncate" title={i.note ?? ''}>
                        {i.note ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                fontSize: 14,
                color: 'var(--adm-muted)',
              }}
            >
              <AdminButton variant="fill" size={32} onClick={() => load(page - 1)} disabled={loading || page <= 1}>
                Trang trước
              </AdminButton>
              <span>
                Trang {page}/{totalPages}
              </span>
              <AdminButton variant="fill" size={32} onClick={() => load(page + 1)} disabled={loading || page >= totalPages}>
                Trang sau
              </AdminButton>
            </div>
          </>
        )}
      </AdminCard>
    </section>
  );
};

export default BrandLedgerSection;
